import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { fetchBriefInsights, regenerateBriefInsights, type AiBriefInsights } from '@apis/aiAgent';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

/**
 * The reader's own insights on this morning's brief, read the same way
 * wherever they are shown — the dashboard's morning card and the sheet's lede.
 *
 * ONE KEY, ON PURPOSE. Both surfaces can be open in the same session, and the
 * first read of the day is a model call on the server: a second key would ask
 * for it twice. Sharing the key makes whichever surface opens second free.
 */
const insightsKey = (siteId: string) => ['aiAgent', 'brief', 'insights', siteId] as const;

export function useBriefInsights(siteId: string): UseQueryResult<AiBriefInsights> {
  return useQuery<AiBriefInsights>({
    queryKey: insightsKey(siteId),
    queryFn: ({ signal }) => fetchBriefInsights(siteId, signal),
    // Written once a day per reader; the server caches them, so there is
    // nothing new to learn by asking again on every focus.
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Write them again. The answer replaces the cached reading, so both surfaces
 * show the new one at once.
 */
export function useRegenerateBriefInsights(siteId: string) {
  const client = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: () => regenerateBriefInsights(siteId),
    onSuccess: (written) => client.setQueryData(insightsKey(siteId), written),
  });
}
